import React, { useState, useEffect, useRef } from "react";
import SEO from "../component/SEO";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { FiSend } from "react-icons/fi";

const UserMessages = () => {
  const user = useSelector((state) => state.user);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const fetchMessages = async (pageNum = 1, isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/user/messages?page=${pageNum}&limit=20`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      const data = await res.json(); // Returns Newest First
      if (res.ok) {
        // Reverse for chronological display: [Older ... Newest]
        const chronologicalData = [...data].reverse();

        if (pageNum === 1) {
            // Initial load or poll
            if (isSilent) {
                // Silent Poll: Merge or replace if newer?
                // Simple strategy: Just update page 1. 
                // To avoid jumpiness if user is scrolled up, we only append *new* messages at the bottom.
                // But for simplicity in this artifact given complex merging:
                // We will append if new messages exist.
                 setMessages(prev => {
                     if (prev.length === 0) return chronologicalData;
                     const lastMsg = prev[prev.length - 1];
                     // Find new messages in chronologicalData that are newer than lastMsg
                     const newItems = chronologicalData.filter(m => new Date(m.createdAt) > new Date(lastMsg.createdAt));
                     if (newItems.length > 0) return [...prev, ...newItems];
                     return prev;
                 });
            } else {
                 setMessages(chronologicalData);
                 // Initial load: Scroll to bottom
                 // setTimeout(() => messagesEndRef.current?.scrollIntoView(), 100); 
                 // Removing auto-scroll as requested, but valid for INITIAL load usually.
                 // User asked to STOP auto scroll. So we leave it.
                 // Actually, on FIRST open (page 1, not silent), it makes sense to see latest.
                 // But let's respect "STOP auto scroll" strictly.
            }
        } else {
            // Loading history (Page > 1) -> Prepend
            const currentScrollHeight = chatContainerRef.current?.scrollHeight || 0;
            setMessages(prev => [...chronologicalData, ...prev]);
            
            // Maintain scroll position
            requestAnimationFrame(() => {
                if (chatContainerRef.current) {
                    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight - currentScrollHeight;
                }
            });
        }
        
        if (data.length < 20) setHasMore(false);
        else setHasMore(true);

      }
    } catch (err) {
      console.error(err);
      if (!isSilent) toast.error("Error loading messages");
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.token) {
        fetchMessages(1);
        // Poll for new messages every 3 seconds
        const interval = setInterval(() => {
            fetchMessages(1, true);
        }, 3000);
        return () => clearInterval(interval);
    }
  }, [user?.token]);

  const handleScroll = (e) => {
      if (e.target.scrollTop === 0 && hasMore && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchMessages(nextPage);
      }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    // Optimistic UI Update
    const tempMessage = {
      _id: Date.now(),
      message: replyText,
      sender: "user",
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMessage]);
    setReplyText("");

    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/submitContactForm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
            name: (user.firstName ? user.firstName : "") + (user.lastName ? " " + user.lastName : "") || (user.email?.split('@')[0] || "User"),
            email: user.email,
            message: tempMessage.message
        }),
      });

      if (!res.ok) {
        toast.error("Failed to send message");
      } else {
        // Fast re-fetch to ensure sync (optional)
        // fetchMessages(1, true); 
      }
      
    } catch (err) {
      console.error("Failed to send message", err);
      toast.error("Network error");
    }
  };

  return (
    <div className="flex flex-col h-screen pt-16 bg-gray-50">
      <div className="max-w-4xl w-full mx-auto flex flex-col h-[calc(100vh-64px)] bg-white shadow-xl md:rounded-xl md:my-4 md:h-[calc(100vh-100px)] overflow-hidden">
        
        {/* Header */}
        <div className="bg-pink-600 p-4 text-center">
            <SEO title="Support Chat - K Bakery" description="Chat with K Bakery support." />
            <h1 className="text-white font-bold text-lg">Support Chat</h1>
            <p className="text-pink-100 text-xs">We typically reply within a few hours.</p>
        </div>

        {/* Chat History */}
        <div 
            ref={chatContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50"
        >
          {loading && page === 1 && (
             <div className="text-center mt-10 text-gray-400">Loading your messages...</div>
          )}
          {loading && page > 1 && (
             <div className="text-center text-xs text-gray-300 py-2">Loading older history...</div>
          )}

          {messages.length === 0 && !loading ? (
             <div className="text-center mt-20 text-gray-400">
                <p>No messages yet.</p>
                <p className="text-sm">Send us a message below to get started!</p>
             </div>
          ) : (
            messages.map((msg, idx) => {
              const isUser = msg.sender === "user";
              return (
                <div key={msg._id || idx} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] md:max-w-[60%] rounded-2xl px-4 py-3 shadow-sm ${
                    isUser 
                      ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-br-none" 
                      : "bg-white text-gray-800 border border-gray-200 rounded-bl-none"
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                    <p className={`text-[10px] mt-1 text-right ${isUser ? "text-pink-100" : "text-gray-400"}`}>
                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white border-t border-gray-200">
          <form onSubmit={handleSend} className="flex items-center gap-2">
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 bg-gray-100 border-0 text-gray-800 rounded-full px-5 py-3 focus:ring-2 focus:ring-pink-500 focus:bg-white transition outline-none"
            />
            <button 
              type="submit" 
              disabled={!replyText.trim()}
              className="bg-pink-600 hover:bg-pink-700 text-white p-3 rounded-full shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition transform active:scale-95"
            >
              <FiSend size={20} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserMessages;
